import { Eye, EyeOff, Loader, Lock, Mail, Phone, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import PageWrapper from "../components/common/PageWrapper";
import logo from "../assets/logo.png";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import CountryCodeInput from './common/CountryCodeInput';
import { withCountryCode } from '../utils/customerPhone';

const inputClass =
  "h-12 w-full border-b border-slate-300 bg-transparent px-0 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-primary-800";

function Field({ icon, label, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
        {icon}
        {label}
      </span>
      {children}
    </label>
  );
}

export default function AuthPage({ initialMode = "signin" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const afterSignIn =
    typeof from === "string" && from.startsWith("/") && !from.startsWith("//")
      ? from
      : "/userprofile";
  const { login, register } = useCustomerAuth();

  const [mode, setMode] = useState(initialMode);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [identifier, setIdentifier] = useState("");
  const [countryCode, setCountryCode] = useState('+91');
  const [password, setPassword] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
  });

  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    setMode(initialMode);
    setError("");
  }, [initialMode]);

  const update = (key) => (event) => {
    setForm((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setShowPass(false);
    setShowConfirm(false);
  };

  const handleSignIn = async (event) => {
    event.preventDefault();

    if (!identifier.trim() || !password) {
      setError("Enter your email or mobile number and password.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await login({
        identifier: withCountryCode(identifier, countryCode),
        password,
      });

      navigate(afterSignIn);
    } catch (requestError) {
      setError(
        String(
          requestError.message ||
            "Could not sign in. Please check your details.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.password) {
      setError("Full name, email and password are required.");
      return;
    }

    if (form.password.length < 8) {
      setError("Password must contain at least 8 characters.");
      return;
    }

    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await register({
        fullName: form.name.trim(),
        email: form.email.trim(),
        phone: withCountryCode(form.phone, countryCode),
        password: form.password,
      });

      navigate(afterSignIn);
    } catch (requestError) {
      setError(
        String(
          requestError.message ||
            "Could not create your account. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <div className="min-h-screen bg-[#faf9f7]">
        <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
          <section className="relative hidden min-h-screen overflow-hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1800&q=90"
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />

            <div className="absolute inset-0 bg-slate-950/65" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/25 to-slate-950/45" />

            <div className="relative z-10 flex h-full flex-col justify-between p-12 xl:p-16">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="flex w-fit items-center gap-3 text-left text-white"
              >
                <img
                  src={logo}
                  alt="CHARMS"
                  className="h-11 w-11 object-contain"
                />

                <div>
                  <p className="font-serif text-[25px] font-semibold leading-none tracking-[0.16em] sm:text-[31px]">
                    CHARMS
                  </p>
                </div>
              </button>

              <div className="max-w-xl">
                <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-white/65">
                  CHARMS Collections
                </p>

                <h1 className="mt-5 text-5xl font-semibold leading-[1.05] tracking-tight text-white xl:text-6xl">
                  Discover pieces
                  <br />
                  made for you.
                </h1>

                <p className="mt-6 max-w-md text-base leading-7 text-white/75">
                  Sign in to shop your favourite labels, manage orders and make
                  every CHARMS purchase more rewarding.
                </p>
              </div>

              <div className="grid max-w-xl grid-cols-3 border border-white/20 bg-white/10 backdrop-blur-sm">
                <div className="border-r border-white/15 p-5">
                  <p className="text-2xl font-semibold text-white">Premium</p>
                  <p className="mt-1 text-xs text-white/60">Curated labels</p>
                </div>

                <div className="border-r border-white/15 p-5">
                  <p className="text-2xl font-semibold text-white">Easy</p>
                  <p className="mt-1 text-xs text-white/60">Order tracking</p>
                </div>

                <div className="p-5">
                  <p className="text-2xl font-semibold text-white">Rewards</p>
                  <p className="mt-1 text-xs text-white/60">Every purchase</p>
                </div>
              </div>
            </div>
          </section>

          <section className="flex min-h-screen items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
            <div className="w-full max-w-md">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="mb-12 flex items-center gap-2 text-left lg:hidden"
              >
                <img
                  src={logo}
                  alt="CHARMS"
                  className="h-9 w-9 object-contain"
                />
                <span className="text-2xl font-semibold tracking-tight text-slate-950">
                  CHARMS
                </span>
              </button>

              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary-800">
                {mode === "signin" ? "Welcome back" : "Join CHARMS"}
              </p>

              <h2 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950">
                {mode === "signin"
                  ? "Sign in to your account"
                  : "Create your account"}
              </h2>

              <p className="mt-3 text-sm leading-6 text-slate-500">
                {mode === "signin"
                  ? "Access orders, saved pieces, rewards and delivery details."
                  : "Create your CHARMS account in just a few moments."}
              </p>

              <div className="mt-8 grid grid-cols-2 border-b border-slate-200">
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className={`border-b-2 px-2 py-3 text-sm font-semibold transition ${
                    mode === "signin"
                      ? "border-primary-800 text-primary-800"
                      : "border-transparent text-slate-500 hover:text-slate-950"
                  }`}
                >
                  Sign In
                </button>

                <button
                  type="button"
                  onClick={() => switchMode("signup")}
                  className={`border-b-2 px-2 py-3 text-sm font-semibold transition ${
                    mode === "signup"
                      ? "border-primary-800 text-primary-800"
                      : "border-transparent text-slate-500 hover:text-slate-950"
                  }`}
                >
                  Create Account
                </button>
              </div>

              {error ? (
                <div className="mt-6 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              ) : null}

              {mode === "signin" ? (
                <form onSubmit={handleSignIn} className="mt-8 space-y-6">
                  <CountryCodeInput value={countryCode} onChange={setCountryCode} disabled={loading} />
                  <Field
                    icon={<Mail size={14} />}
                    label="Email address or mobile number"
                  >
                    <input
                      type="text"
                      value={identifier}
                      onChange={(event) => setIdentifier(event.target.value)}
                      placeholder="Email or mobile number"
                      autoComplete="username"
                      className={inputClass}
                    />
                  </Field>

                  <Field icon={<Lock size={14} />} label="Password">
                    <div className="relative">
                      <input
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        className={`${inputClass} pr-10`}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPass((current) => !current)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-950"
                        aria-label={
                          showPass ? "Hide password" : "Show password"
                        }
                      >
                        {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </Field>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-xs font-bold uppercase tracking-[0.1em] text-primary-800 hover:text-primary-950"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex h-13 w-full items-center justify-center gap-2 bg-slate-950 px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Signing In
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSignUp} className="mt-8 space-y-6">
                  <Field icon={<User size={14} />} label="Full name">
                    <input
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      autoComplete="name"
                      className={inputClass}
                    />
                  </Field>

                  <Field icon={<Mail size={14} />} label="Email address">
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      autoComplete="email"
                      className={inputClass}
                    />
                  </Field>

                  <Field
                    icon={<Phone size={14} />}
                    label="Mobile number (optional, with country code)"
                  >
                    <CountryCodeInput value={countryCode} onChange={setCountryCode} disabled={loading} />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="9876543210"
                      autoComplete="tel"
                      className={inputClass}
                    />
                  </Field>

                  <Field icon={<Lock size={14} />} label="Password">
                    <div className="relative">
                      <input
                        type={showPass ? "text" : "password"}
                        value={form.password}
                        onChange={update("password")}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        className={`${inputClass} pr-10`}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPass((current) => !current)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-950"
                        aria-label={
                          showPass ? "Hide password" : "Show password"
                        }
                      >
                        {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </Field>

                  <Field icon={<Lock size={14} />} label="Confirm password">
                    <div className="relative">
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={form.confirm}
                        onChange={update("confirm")}
                        placeholder="Repeat your password"
                        autoComplete="new-password"
                        className={`${inputClass} pr-10`}
                      />

                      <button
                        type="button"
                        onClick={() => setShowConfirm((current) => !current)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-950"
                        aria-label={
                          showConfirm ? "Hide password" : "Show password"
                        }
                      >
                        {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </Field>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 bg-slate-950 px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Creating Account
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </button>
                </form>
              )}

              <div className="mt-10 border-t border-slate-200 pt-5">
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  <span>Secure account</span>
                  <span>Genuine brands</span>
                  <span>Easy returns</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </PageWrapper>
  );
}
